"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePopover = exports.useMeasureClientRect = void 0;
const react_1 = __importStar(require("react"));
const react_dom_1 = require("react-dom");
const element_resize_event_1 = __importDefault(require("element-resize-event"));
const helpers_1 = require("../helpers");
const reflect_1 = require("../helpers/reflect");
const useOutSideClick_1 = require("../helpers/useOutSideClick");
const zIndexManager_1 = require("../helpers/zIndexManager");
const ModalPopoverContext_1 = require("../Modal/context/ModalPopoverContext");
const helpers_2 = require("./helpers");
const selectSupportValue = (values) => {
    for (const value in values) {
        const element = document.createElement('b');
        element.style.display = value;
        if (element.style.display === value) {
            return value;
        }
    }
    return values[0];
};
const INLINE_FLEX_VALUE = selectSupportValue([
    'inline-flex',
    '-ms-inline-flexbox',
    '-webkit-inline-box'
]);
const useMeasureClientRect = (ref) => {
    const listenersRef = (0, react_1.useRef)([]);
    const nodeRef = (0, react_1.useRef)(null);
    const domNodeRef = ref || nodeRef;
    const getDOMNode = (0, react_1.useCallback)(() => {
        const domNode = (0, reflect_1.isHTMLElement)(domNodeRef.current)
            ? domNodeRef.current
            : // eslint-disable-next-line react/no-find-dom-node
                (0, react_dom_1.findDOMNode)((0, reflect_1.isComponent)(domNodeRef.current) ? domNodeRef.current : null);
        return domNode;
    }, [domNodeRef]);
    const measure = () => {
        if (!domNodeRef.current) {
            return null;
        }
        const domNode = getDOMNode();
        if (!domNode || typeof domNode.getBoundingClientRect !== 'function') {
            return null;
        }
        const rect = domNode.getBoundingClientRect();
        return {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height
        };
    };
    (0, react_1.useEffect)(() => {
        const domNode = getDOMNode();
        const handler = () => {
            listenersRef.current.forEach((cb) => cb());
        };
        if (domNode) {
            (0, element_resize_event_1.default)(domNode, handler);
        }
        return () => {
            if (domNode) {
                element_resize_event_1.default.unbind(domNode, handler);
            }
        };
    }, [getDOMNode]);
    return [
        domNodeRef,
        {
            measure,
            getDOMNode,
            listenResize: (cb) => {
                listenersRef.current.push(cb);
                return () => {
                    listenersRef.current = listenersRef.current.filter((i) => i !== cb);
                };
            }
        }
    ];
};
exports.useMeasureClientRect = useMeasureClientRect;
const PopoverContext = react_1.default.createContext({
    getTargetElements: null
});
function PopoverZStackManagerContainer({ children, onKeyPress, getElements, isActive, onOutsideClick }) {
    (0, zIndexManager_1.useZIndexManager)({
        onKeyPress
    });
    const childrenContextRef = (0, react_1.useRef)({
        getTargetElements: null
    });
    const popoverContext = (0, react_1.useContext)(PopoverContext);
    (0, react_1.useEffect)(() => {
        popoverContext.getTargetElements = () => getElements();
        return () => {
            popoverContext.getTargetElements = null;
        };
    }, [getElements, popoverContext]);
    (0, useOutSideClick_1.useOutSideClick)({
        isActive,
        getElements: () => {
            var _a, _b, _c;
            return [
                ...getElements(),
                ...((_c = (_b = (_a = childrenContextRef.current) === null || _a === void 0 ? void 0 : _a.getTargetElements) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : [])
            ];
        },
        onClick: (e) => {
            onOutsideClick === null || onOutsideClick === void 0 ? void 0 : onOutsideClick(e);
        }
    });
    return react_1.default.createElement(PopoverContext.Provider, { value: childrenContextRef.current }, children || null);
}
const usePopover = ({ config, isActive = true, viewportAccuracyFactor = 1, zIndex = helpers_1.POPOVER_Z_INDEX, target, render, ref, onKeyPress, onWindowScroll, onOutsideClick, getAdditionalContentNode = () => [] }) => {
    let containerRef = (0, react_1.useRef)(null);
    containerRef = ref ? ref : containerRef;
    const portalDOMNodeRef = (0, helpers_2.usePortalDOMNodeRef)(zIndex);
    const modalPopoverContext = (0, react_1.useContext)(ModalPopoverContext_1.ModalPopoverContext);
    const removePopoverFormModalRef = (0, react_1.useRef)(() => { });
    const [popoverRef, popoverRectActions] = (0, exports.useMeasureClientRect)();
    const updateStyle = (0, react_1.useCallback)(() => {
        if (!isActive) {
            return;
        }
        const actualPopoverRect = popoverRectActions.measure();
        const actualTargetRect = target.measure();
        const portalPosition = portalDOMNodeRef.current
            ? portalDOMNodeRef.current.getBoundingClientRect()
            : null;
        if (containerRef.current && actualTargetRect && portalPosition && actualPopoverRect) {
            const windowSize = (0, helpers_2.getWindowSize)();
            const preset = (0, helpers_2.selectBestPreset)({
                presets: config,
                targetRect: actualTargetRect,
                popoverRect: actualPopoverRect,
                windowSize,
                portalPosition,
                viewportAccuracyFactor
            });
            Object.assign(containerRef.current.style, (0, helpers_2.calculateStyle)({
                preset,
                targetRect: actualTargetRect,
                popoverRect: actualPopoverRect,
                portalPosition,
                offset: { top: preset.offsetY || 0, left: preset.offsetX || 0 },
                windowSize
            }));
        }
    }, [config, isActive, popoverRectActions, portalDOMNodeRef, target, viewportAccuracyFactor]);
    (0, react_1.useEffect)(() => {
        let removeListenResize;
        if (target.listenResize) {
            removeListenResize = target.listenResize(() => {
                updateStyle();
            });
        }
        if (typeof window !== 'undefined') {
            const listener = () => {
                updateStyle();
            };
            if (onWindowScroll) {
                window.addEventListener('scroll', onWindowScroll, true);
            }
            window.addEventListener('resize', listener, true);
            window.addEventListener('scroll', listener, true);
            if (containerRef.current && isActive) {
                removePopoverFormModalRef.current = modalPopoverContext.addChild(containerRef.current);
            }
            return () => {
                if (removeListenResize) {
                    removeListenResize();
                }
                if (removePopoverFormModalRef.current) {
                    removePopoverFormModalRef.current();
                }
                window.removeEventListener('resize', listener, true);
                window.removeEventListener('scroll', listener, true);
                if (onWindowScroll) {
                    window.removeEventListener('scroll', onWindowScroll, true);
                }
            };
        }
        return () => {
            if (removePopoverFormModalRef.current) {
                removePopoverFormModalRef.current();
            }
            if (removeListenResize) {
                removeListenResize();
            }
        };
    }, [onWindowScroll, isActive, modalPopoverContext, target, updateStyle]);
    (0, react_1.useLayoutEffect)(() => {
        updateStyle();
    }, [updateStyle]);
    (0, react_1.useEffect)(() => {
        setTimeout(() => {
            updateStyle();
        }, 0);
    }, [updateStyle]);
    (0, react_1.useEffect)(() => {
        return () => {
            if (removePopoverFormModalRef.current) {
                removePopoverFormModalRef.current();
            }
        };
    }, []);
    if (!isActive || !portalDOMNodeRef.current) {
        return null;
    }
    return (0, react_dom_1.createPortal)(react_1.default.createElement(PopoverZStackManagerContainer, {
        onKeyPress,
        isActive: isActive && !!onOutsideClick,
        getElements: () => {
            return [containerRef.current, ...getAdditionalContentNode()].filter(Boolean);
        },
        onOutsideClick
    }, react_1.default.createElement('div', {
        tabIndex: -1,
        'data-selector-id': 'popover-container',
        'aria-modal': 'true',
        style: { outline: 'none', zIndex: helpers_1.POPOVER_Z_INDEX },
        ref: (node) => {
            containerRef.current = node;
            if (node) {
                Object.assign(containerRef.current.style, {
                    display: INLINE_FLEX_VALUE,
                    visibility: 'hidden'
                });
            }
        }
    }, render(popoverRef))), portalDOMNodeRef.current);
};
exports.usePopover = usePopover;
//# sourceMappingURL=hooks.js.map